import React from "react";
import {
    Box, Container, Heading, Text, VStack, HStack, Stack, SimpleGrid, Icon, Flex,
    Tabs, TabList, TabPanels, Tab, TabPanel, Divider,
} from "@chakra-ui/react";
import { FiCheckCircle, FiClock, FiGift, FiStar, FiTrendingUp } from "react-icons/fi";
import { ModuleConfig } from "../../../types/ModuleConfig";
import { ChangelogEntry, RoadmapItem } from "../types";
import {
    FLOOR_SX, FadeIn, PageHeader, SectionHeading, Stagger, StaggerItem, StatusPill, Surface,
    Tip, onDate, useOppTheme, words,
} from "./ui";

interface ModuleWhatsNewProps {
    moduleConfig: ModuleConfig;
}

/**
 * "What's new" for a module: what shipped, and what is coming. POLISH v1 pass 2026-10-17.
 *
 * Module-local by design — five modules each keep their own copy of this component, so polishing this
 * one touches nothing else.
 *
 * What the pass changed. The page used to print `gray.500` / `gray.600` body text on a dark surface
 * (unreadable), raw roadmap states (`planned`, `in-progress`), and a priority badge whose only cue was
 * a colour plus an emoji. Changelog lines were a bare `<List>` with no bullet, and every feature string
 * carried its own hard-coded "✅" because nothing else marked it as done.
 */
export const ModuleWhatsNew: React.FC<ModuleWhatsNewProps> = ({ moduleConfig }) => {
    const t = useOppTheme();

    const changelog = moduleConfig.changelog ?? [];
    const roadmap = moduleConfig.roadmap ?? [];

    const priorityWords: Record<string, { label: string; tone: "danger" | "warning" | "accent" }> = {
        high: { label: "Next up", tone: "danger" },
        medium: { label: "After that", tone: "warning" },
        low: { label: "Someday", tone: "accent" },
    };

    const statusIcon: Record<string, typeof FiCheckCircle> = {
        completed: FiCheckCircle,
        "in-progress": FiClock,
        planned: FiStar,
    };

    return (
        <Box bg={t.colorMode === "dark" ? undefined : undefined} sx={FLOOR_SX}>
            <Container maxW="container.xl" py={{ base: 6, md: 8 }} px={{ base: 4, md: 8 }}>
                <VStack align="stretch" spacing={6}>
                    <PageHeader
                        icon={FiGift}
                        eyebrow={moduleConfig.name}
                        title="What's new"
                        subtitle={moduleConfig.description}
                        badges={<StatusPill label={`Version ${moduleConfig.version}`} tone="accent" icon={FiTrendingUp} />}
                    />

                    <Tabs colorScheme="blue" isLazy>
                        <TabList borderBottomColor={t.border}>
                            {/* 16px tab labels with words and a count — not a 12px pill. */}
                            <Tab fontSize="md" fontWeight="semibold" minH="48px" color={t.muted} _selected={{ color: t.primary, borderColor: t.primary }}>
                                What shipped ({changelog.length})
                            </Tab>
                            <Tab fontSize="md" fontWeight="semibold" minH="48px" color={t.muted} _selected={{ color: t.primary, borderColor: t.primary }}>
                                What's coming ({roadmap.length})
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* ---------------------------------------------------------- what shipped */}
                            <TabPanel px={0} pt={6}>
                                {changelog.length === 0 ? (
                                    <Surface textAlign="center" py={10}>
                                        <Heading size="md" color={t.text}>No version history yet</Heading>
                                        <Text fontSize="md" color={t.muted} mt={2}>
                                            Each release will be listed here, newest first, with what it changed.
                                        </Text>
                                    </Surface>
                                ) : (
                                    <Stagger>
                                        <VStack align="stretch" spacing={5}>
                                            {changelog.map((entry: ChangelogEntry, idx: number) => (
                                                <StaggerItem key={entry.version}>
                                                    <Surface borderColor={idx === 0 ? t.primary : t.border}>
                                                        <Stack
                                                            direction={{ base: "column", sm: "row" }}
                                                            justify="space-between" align={{ base: "stretch", sm: "center" }}
                                                            spacing={3} mb={3}
                                                        >
                                                            <HStack spacing={3} flexWrap="wrap">
                                                                <Heading size="md" color={t.text}>Version {entry.version}</Heading>
                                                                {idx === 0 && <StatusPill label="You are on this one" tone="success" />}
                                                            </HStack>
                                                            <Text fontSize="md" color={t.muted} flexShrink={0}>
                                                                {onDate(entry.releaseDate)}
                                                            </Text>
                                                        </Stack>

                                                        <Heading as="h3" size="sm" color={t.text} mb={3}>{entry.title}</Heading>
                                                        <Divider borderColor={t.border} mb={3} />

                                                        <VStack as="ul" align="stretch" spacing={2} listStyleType="none">
                                                            {entry.features.map((feature: string) => (
                                                                // The data carries a leading "✅" from when nothing else marked a
                                                                // shipped line; the tick is drawn now, so strip it.
                                                                <HStack as="li" key={feature} align="start" spacing={3}>
                                                                    <Icon as={FiCheckCircle} color={t.success} mt={1} flexShrink={0} />
                                                                    <Text fontSize="md" color={t.muted}>
                                                                        {feature.replace(/^\s*[✅✔️☑️]\s*/u, "")}
                                                                    </Text>
                                                                </HStack>
                                                            ))}
                                                        </VStack>

                                                        {entry.notes && (
                                                            <Box mt={4} p={4} borderRadius="xl" bg={t.raised} borderLeftWidth="4px" borderLeftColor={t.primary}>
                                                                <Text fontSize="md" color={t.muted}>{entry.notes}</Text>
                                                            </Box>
                                                        )}
                                                    </Surface>
                                                </StaggerItem>
                                            ))}
                                        </VStack>
                                    </Stagger>
                                )}
                            </TabPanel>

                            {/* --------------------------------------------------------- what's coming */}
                            <TabPanel px={0} pt={6}>
                                {roadmap.length === 0 ? (
                                    <Surface textAlign="center" py={10}>
                                        <Heading size="md" color={t.text}>Nothing planned here yet</Heading>
                                        <Text fontSize="md" color={t.muted} mt={2}>
                                            Planned work will be listed here once it is decided.
                                        </Text>
                                    </Surface>
                                ) : (
                                    <Stagger>
                                        <VStack align="stretch" spacing={5}>
                                            {roadmap.map((item: RoadmapItem) => {
                                                const priority = priorityWords[item.priority] ?? priorityWords.low;
                                                return (
                                                    <StaggerItem key={item.title}>
                                                        <Surface>
                                                            <SectionHeading
                                                                icon={statusIcon[item.status] ?? FiStar}
                                                                title={item.title}
                                                                why={item.description}
                                                                tone={item.status === "completed" ? "success" : item.status === "in-progress" ? "warning" : "accent"}
                                                                right={
                                                                    <HStack spacing={2} flexWrap="wrap" justify={{ base: "flex-start", sm: "flex-end" }}>
                                                                        <StatusPill vocab="roadmapStatus" value={item.status} />
                                                                        <Tip label="How soon this is likely to be built — not a promised date.">
                                                                            <Box><StatusPill label={priority.label} tone={priority.tone} /></Box>
                                                                        </Tip>
                                                                        {item.targetVersion && (
                                                                            <StatusPill label={`Version ${item.targetVersion}`} tone="purple" />
                                                                        )}
                                                                    </HStack>
                                                                }
                                                            />
                                                            <Divider borderColor={t.border} mb={3} />
                                                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={2}>
                                                                {item.features.map((feature: string) => (
                                                                    <HStack key={feature} align="start" spacing={3}>
                                                                        <Flex flexShrink={0} mt={1}>
                                                                            <Icon
                                                                                as={statusIcon[item.status] ?? FiStar}
                                                                                color={item.status === "completed" ? t.success : t.faint}
                                                                            />
                                                                        </Flex>
                                                                        <Text fontSize="md" color={t.muted}>{feature}</Text>
                                                                    </HStack>
                                                                ))}
                                                            </SimpleGrid>
                                                        </Surface>
                                                    </StaggerItem>
                                                );
                                            })}
                                        </VStack>
                                    </Stagger>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>

                    <FadeIn>
                        <Text fontSize="sm" color={t.faint} textAlign="center">
                            Dates and versions come from the module's own record, so this page is never out of step with
                            what is installed. {words("roadmapStatus", "planned").label} items are intentions, not commitments.
                        </Text>
                    </FadeIn>
                </VStack>
            </Container>
        </Box>
    );
};

export default ModuleWhatsNew;
